<template>
  <div
    class="bonus-active"
    data-tooltip-parent
  >
    <div class="bonus-active__list">
      <CardBonuses
        v-for="bonus in orderedBonuses"
        :key="bonus.id"
        :bonus="bonus"
        :mode="bonusType"
        :content="content"
        :default-locale-content="defaultLocaleContent"
        @switch-bonus="changeBonuses(bonus, 'activate')"
        @remove-bonus="changeBonuses(bonus, 'cancel')"
        @get-game-info="modalState.gameInfo = $event"
        @show-conditions="showConditionsModal(bonus)"
      />
    </div>

    <bottom-sheet-confirm-bonus
      v-if="isMobile"
      v-bind="modalState"
      ref="bsConfirmBonus"
      :bonuses-updating="bonusesUpdating"
      @confirm="confirmAction"
    >
      <div class="bonus-active__bottom-sheet-text">
        {{ interpolatedDescription }}
      </div>
    </bottom-sheet-confirm-bonus>

    <modal-confirm-bonus
      v-if="!isMobile"
      v-bind="modalState"
      :interpolated-description="interpolatedDescription"
      :show-modal="showModalConfirmBonus"
      :bonuses-updating="bonusesUpdating"
      @close-modal="showModalConfirmBonus = false"
      @confirm="confirmAction"
    />

    <bottom-sheet-confirm-bonus-unsettled
      v-if="isMobile"
      v-bind="modalState"
      ref="bsConfirmBonusUnsettled"
      :bonuses-updating="bonusesUpdating"
      @confirm="confirmAction"
    />

    <modal-confirm-bonus-unsettled
      v-if="!isMobile"
      v-bind="modalState"
      :show-modal="showConfirmBonusUnsettled"
      :bonuses-updating="bonusesUpdating"
      @close-modal="showConfirmBonusUnsettled = false"
      @confirm="confirmAction"
    />

    <bottom-sheet-bonus-condition
      v-if="isMobile"
      ref="bsBonuscondition"
      v-bind="modalState"
    />

    <modal-bonus-condition
      v-if="!isMobile"
      :show-modal="showModalBonusCondition"
      v-bind="modalState"
      @close-modal="showModalBonusCondition = false"
    />
  </div>
</template>

<script setup lang="ts">
import type { IGame, IPlayerBonus, IPlayerFreeSpin, IBottomSheetComponent } from '@skeleton/core/types';
import type { IPageBonusesContent } from '~/types';

const walletStore = useWalletStore();
const { accounts, activeAccount } = storeToRefs(walletStore);
const { switchAccount } = useWalletStore();
const bonusCurrency = ref('');
const props = defineProps<{
  content?: Maybe<IPageBonusesContent>;
  defaultLocaleContent?: Maybe<IPageBonusesContent>;
  bonusType: 'bonus' | 'free-spin';
}>();

interface IModalState extends Record<string, any> {
  image?: string;
  title?: string;
  description?: string;
  switchActivate?: string;
  wageringLabel?: string;
  confirmButton?: string;
  cancelButton?: string;
  bonusInfo?: IPlayerBonus | IPlayerFreeSpin | undefined;
  mode: 'activate' | 'cancel';
  bonusType?: 'bonus' | 'free-spin';
  gameInfo?: IGame | undefined;
}

const modalState = reactive<IModalState>({
  image: undefined,
  title: undefined,
  description: undefined,
  switchActivate: undefined,
  wageringLabel: undefined,
  confirmButton: undefined,
  cancelButton: undefined,
  bonusInfo: undefined,
  mode: 'activate',
  bonusType: props.bonusType,
} as IModalState);

const showModalConfirmBonus = ref(false);
const showConfirmBonusUnsettled = ref(false);
const showModalBonusCondition = ref(false);
const bsBonuscondition = ref<Maybe<IBottomSheetComponent>>();
const bsConfirmBonusUnsettled = ref<Maybe<IBottomSheetComponent>>();
const bsConfirmBonus = ref<Maybe<IBottomSheetComponent>>();

const interpolatedDescription = computed(() => {
  return modalState.description?.replace('{{currency}}', bonusCurrency.value ?? '');
});

const deviceStore = useDeviceStore();
const { isMobile } = storeToRefs(deviceStore);

const bonusStore = useBonusStore();
const { playerBonuses, playerFreeSpins } = storeToRefs(bonusStore);
const orderedBonuses = computed(() => {
  if (props.bonusType === 'bonus') {
    return playerBonuses.value.reduce((acc: IPlayerBonus[], currentBonus) => {
      return currentBonus.status === 2 ? [currentBonus, ...acc] : [...acc, currentBonus];
    }, []);
  }

  return playerFreeSpins.value.reduce((acc: IPlayerFreeSpin[], currentFreeSpin) => {
    return currentFreeSpin.status === 2 ? [currentFreeSpin, ...acc] : [...acc, currentFreeSpin];
  }, []);
});

const { showAlert } = useLayoutStore();
const globalStore = useGlobalStore();
const { layoutData, defaultLocaleLayoutData } = storeToRefs(globalStore);
const { getContent } = useProjectMethods();
const getButtonText = (key: string) => getContent(layoutData.value, defaultLocaleLayoutData.value, `buttons.${key}`);
const { alertsData, defaultLocaleAlertsData, popupsData, defaultLocalePopupsData } = storeToRefs(globalStore);

const setModalState = (data: any): void => {
  modalState.title = data.title;
  modalState.description = data.description;
  modalState.confirmButton = getButtonText('switchActivate');
  modalState.cancelButton = getButtonText('no');
};

const setModalStateForUnsettledBonus = (): void => {
  const data = popupsData.value?.cancelBonusUnsettled || defaultLocalePopupsData?.value?.cancelBonusUnsettled;
  if (data) {
    modalState.image = data.image;
    modalState.title = data.title;
    modalState.description = data.description;
    modalState.wageringLabel = data.wageringLabel;
    modalState.confirmButton = getButtonText('yes');
    modalState.cancelButton = getButtonText('no');
  }
};

const setModalStateForActiveBonus = (): void => {
  const data = popupsData.value?.changeActiveBonus || defaultLocalePopupsData?.value?.changeActiveBonus;
  if (data) setModalState(data);
};

const setModalStateForCancelBonus = (processBonus: IPlayerBonus | IPlayerFreeSpin): void => {
  const data = popupsData.value?.cancelBonus || defaultLocalePopupsData?.value?.cancelBonus;
  if (data) {
    modalState.title = data.title;
    modalState.description
        = processBonus.openedTransactionsCount > 0 ? data.issuedBonusDescription : data.activeBonusDescription;
    modalState.confirmButton = getButtonText('yes');
    modalState.cancelButton = getButtonText('no');
  }
};

const changeBonuses = (processBonus: IPlayerBonus | IPlayerFreeSpin, processMode: 'activate' | 'cancel'): void => {
  bonusCurrency.value = processBonus.currency;
  modalState.bonusInfo = processBonus;
  modalState.mode = processMode;

  if (processBonus.status === 1) {
    if (processMode === 'activate') {
      setModalStateForActiveBonus();
    }
    else {
      setModalStateForCancelBonus(processBonus);
    }

    toggleConfirmBonus('open');
  }
  else if (processBonus.openedTransactionsCount > 0) {
    setModalStateForUnsettledBonus();
    toggleConfirmBonusUnsettled('open');
  }
  else {
    setModalStateForCancelBonus(processBonus);
    toggleConfirmBonus('open');
  }
};

const bonusesUpdating = ref(false);
const { activatePlayerBonus, cancelPlayerBonus, activatePlayerFreeSpin, cancelPlayerFreeSpin } = useCoreBonusApi();

const activateBonus = async (): Promise<void> => {
  if (bonusesUpdating.value || !modalState.bonusInfo?.id) return;
  bonusesUpdating.value = true;

  try {
    if (props.bonusType === 'bonus') {
      await activatePlayerBonus(modalState.bonusInfo.id);
    }
    else {
      await activatePlayerFreeSpin(modalState.bonusInfo.id);
    }

    toggleConfirmBonus('close');
  }
  catch {
    showAlert(
      alertsData.value?.global?.somethingWrong || defaultLocaleAlertsData.value?.global?.somethingWrong,
      'error',
    );
  }

  bonusesUpdating.value = false;
};

const cancelBonus = async (): Promise<void> => {
  if (bonusesUpdating.value || !modalState.bonusInfo?.id) return;
  bonusesUpdating.value = true;

  try {
    if (props.bonusType === 'bonus') {
      await cancelPlayerBonus(modalState.bonusInfo.id);
    }
    else {
      await cancelPlayerFreeSpin(modalState.bonusInfo.id);
    }

    toggleConfirmBonus('close');
  }
  catch {
    showAlert(
      alertsData.value?.global?.somethingWrong || defaultLocaleAlertsData.value?.global?.somethingWrong,
      'error',
    );
  }

  bonusesUpdating.value = false;
};

const confirmAction = async () => {
  const isActivatable = modalState.bonusInfo?.status === 1 && modalState.mode === 'activate';

  if (!isActivatable) {
    return cancelBonus();
  }
  activateBonus();
  const bonusCurrencyAccount = accounts.value.find(account => account.currency === bonusCurrency.value);
  const isDifferentAccount = bonusCurrencyAccount && bonusCurrencyAccount.id !== activeAccount.value?.id;

  if (isDifferentAccount) {
    await switchAccount(bonusCurrencyAccount.id);
  }
};

const toggleConfirmBonus = (methodName: 'open' | 'close') => {
  bsConfirmBonus.value?.[methodName]();
  showModalConfirmBonus.value = methodName === 'open';
};

const toggleConfirmBonusUnsettled = (methodName: 'open' | 'close') => {
  bsConfirmBonusUnsettled.value?.[methodName]();
  showConfirmBonusUnsettled.value = methodName === 'open';
};

const showConditionsModal = (bonus: IPlayerBonus | IPlayerFreeSpin): void => {
  modalState.bonusInfo = bonus;
  showModalBonusCondition.value = true;
  bsBonuscondition.value?.open();
};
</script>

<style src="~/assets/styles/components/bonus/active.scss" lang="scss" />
